import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ShieldCheck, Star } from "lucide-react";
import { useLang, UI } from "../i18n";
import { HOME, PROJECTS, IMG, THEMES, TRAVEL, TESTIMONIALS, ALUMNI, getProject } from "../content";
import { useLenis, Reveal, MaskLines } from "../components/motion";
import { MiniSite, CourtLines, MonoTag, PlaneIcon } from "../components/motifs";
import { Countdown } from "../components/Countdown";
import { ContactSection } from "../components/Layout";

const DEPARTURES = [
  { code: "AST-01", name: "Skyhook Prep", dest: "STRASBOURG · SXB", to: "/programmes/sky-hook", color: "#A78BFA", status: "boarding" },
  { code: "AST-02", name: "Parisian Prep", dest: "PARIS · CDG", to: "/programmes/parisian-prep", color: "#2BD1E0", status: "ontime" },
  { code: "AST-03", name: "Academy of Basketball", dest: "INTERNATIONAL", to: "/programmes/academy-of-basketball", color: "#FFB81C", status: "boarding" },
  { code: "AST-04", name: "European Team U13/U15", dest: "PARIS · TEC", to: "/programmes/european-team", color: "#F87171", status: "ontime" },
  { code: "AST-05", name: "Immersion Paris", dest: "PARIS · CDG", to: "/immersions/paris", color: "#38BDF8", status: "ontime" },
  { code: "AST-06", name: "Immersion Montréal", dest: "MONTRÉAL · YUL", to: "/immersions/montreal", color: "#5B9BFF", status: "boarding" },
  { code: "AST-07", name: "Immersion Toronto", dest: "TORONTO · YYZ", to: "/immersions/toronto", color: "#FF6B6B", status: "ontime" },
  { code: "AST-08", name: "Immersion Carthagène", dest: "CARTHAGÈNE · CTG", to: "/immersions/carthagene", color: "#FFB35C", status: "boarding" },
  { code: "AST-09", name: "The European Circuit", dest: "PAR · BCN · MIL · LON", to: "/ligue", color: "#CFAB7A", status: "ontime" },
  { code: "AST-10", name: "Winter Cup", dest: "INDOOR · DEC", to: "/evenements", color: "#A4C8E1", status: "boarding" },
  { code: "AST-11", name: "Sherbrooke BC", dest: "SHERBROOKE · QC", to: "/sherbrooke", color: "#FFC72C", status: "ontime" },
  { code: "AST-12", name: "Legacy 3R", dest: "TROIS-RIVIÈRES · QC", to: "/legacy-3r", color: "#FF8A3D", status: "ontime" },
];

const ROUTE = ["PAR", "YUL", "YYZ", "CTG"];

const DAY_NAMES = {
  fr: ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"],
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  es: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"],
};

const AlumniSpotlight = () => {
  const { t } = useLang();
  const stars = ALUMNI.stars.filter((s) => s.img);
  const star = stars[Math.floor(Date.now() / 864e5) % stars.length];
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-6" data-testid="alumni-spotlight">
      <Reveal>
        <div className="relative overflow-hidden rounded-lg border border-white/10 grid grid-cols-1 lg:grid-cols-12"
          style={{ backgroundColor: "#0B0E16" }}>
          <div aria-hidden className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse 55% 60% at 25% -10%, #F6B02C33, transparent 65%)" }} />
          <div className="relative lg:col-span-4 min-h-[300px] flex items-end justify-center pt-8">
            <img src={star.img} alt={star.name}
              className="relative z-10 w-[220px] aspect-[3/4] object-cover object-top rounded-t-md"
              style={{ boxShadow: "0 -10px 70px #F6B02C40" }} />
            <div aria-hidden className="absolute bottom-0 inset-x-0 h-14"
              style={{ background: "linear-gradient(to top, #0B0E16, transparent)" }} />
          </div>
          <div className="relative z-10 lg:col-span-8 p-8 md:p-10">
            <MonoTag style={{ color: "#F6B02C" }}>
              ★ {t({ fr: "Alumni en vedette · Ils ont réussi grâce au parcours AST", en: "Featured alumni · They made it through the AST pathway", es: "Alumni destacado · Lo lograron gracias al recorrido AST" })} ★
            </MonoTag>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-none mt-4">{star.name}</h2>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] mt-3" style={{ color: "#F6B02C" }}>{t(star.current)}</p>
            <p className="font-body text-white/75 mt-5 max-w-2xl">
              {t(star.journey[0])} → {t(star.journey[star.journey.length - 1])}
            </p>
            <Link to="/alumni" data-testid="alumni-spotlight-cta"
              className="inline-flex items-center gap-2 mt-7 rounded-full px-7 py-3.5 font-heading font-semibold uppercase text-sm tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
              style={{ backgroundColor: "#F6B02C" }}>
              {t({ fr: "Découvrir tous nos alumni", en: "Discover all our alumni", es: "Descubrir todos nuestros alumni" })} <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

const WinterCupFeature = () => {
  const { t } = useLang();
  const p = getProject("winter-cup");
  const th = p.theme;
  return (
    <section className="py-20 md:py-28 max-w-[1400px] mx-auto px-5 md:px-10" data-testid="winter-cup-feature">
      <Reveal>
        <MonoTag className="blink" style={{ color: th.accent }}>
          ★ {t({ fr: "Le grand projet des 3 prochains mois", en: "The big project of the next 3 months", es: "El gran proyecto de los próximos 3 meses" })} ★
        </MonoTag>
        <div className="mt-6 relative overflow-hidden rounded-lg border border-white/10 grid grid-cols-1 lg:grid-cols-12"
          style={{ backgroundColor: th.bg }}>
          <div className="relative z-10 lg:col-span-8 p-8 md:p-12 flex flex-col justify-center">
            <div className="flex flex-wrap items-center gap-3">
              <MonoTag className="border border-white/25 rounded-full px-3 py-1.5 text-white/70">{p.code}</MonoTag>
              <MonoTag style={{ color: th.accent }}>{t(p.tag)}</MonoTag>
            </div>
            <h2 className="font-display uppercase leading-[0.85] text-4xl sm:text-5xl lg:text-7xl tracking-tight mt-5">{p.name}</h2>
            <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] mt-4" style={{ color: th.accent }}>
              ATHLETICA · EAUBONNE (95) · 26 → 30 DÉC
            </p>
            <div className="flex flex-wrap gap-3 mt-6" data-testid="winter-cup-feature-categories">
              {p.categories.map((c) => (
                <span key={c.label.fr}
                  className="inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em]"
                  style={c.isNew ? { borderColor: th.accent, color: th.accent } : { borderColor: "rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.75)" }}>
                  {t(c.label)}
                  {c.isNew && <span className="blink font-semibold">· {t({ fr: "NOUVEAU", en: "NEW", es: "NUEVO" })}</span>}
                </span>
              ))}
            </div>
            <p className="font-body text-sm mt-5 inline-flex items-center gap-2 rounded-md border px-4 py-3"
              style={{ borderColor: `${th.accent}55`, color: "rgba(255,255,255,0.85)" }}>
              <span className="blink font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: th.accent }}>
                Early Bird{(() => { const d = Math.ceil((new Date("2026-10-15T23:59:59") - new Date()) / 864e5); return d > 0 ? ` · J-${d}` : ""; })()}
              </span>
              {t({ fr: "Dès 190 € avant le 15 octobre · paiement en 2 ou 3 fois sans frais", en: "From €190 before October 15 · pay in 2 or 3 instalments at no extra cost", es: "Desde 190 € antes del 15 de octubre · pago en 2 o 3 plazos sin recargo" })}
            </p>
            <div className="mt-8">
              <Link to="/evenements" data-testid="winter-cup-feature-cta"
                className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-heading font-semibold uppercase text-sm tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
                style={{ backgroundColor: th.accent }}>
                {t({ fr: "Découvrir la Winter Cup", en: "Discover the Winter Cup", es: "Descubrir la Winter Cup" })} <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
          <div className="relative lg:col-span-4 flex flex-col items-center justify-center gap-5 p-8 md:p-10">
            <div className="relative overflow-hidden rounded-lg border border-white/10 w-full" style={{ aspectRatio: "16 / 9" }}>
              <iframe src={p.video} title="The Winter Cup — video" data-testid="winter-cup-feature-video"
                className="absolute inset-0 w-full h-full" frameBorder="0"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen />
            </div>
            <img src={p.logo} alt="Winter Cup" className="w-24 md:w-28 rounded-xl bg-white p-2 shadow-2xl -rotate-3" />
          </div>
        </div>
      </Reveal>
    </section>
  );
};

const ProjectOfDay = () => {
  const { t } = useLang();
  const now = new Date();
  const day = now.getDay();
  const week = Math.floor(now.getTime() / (7 * 24 * 3600 * 1000));
  const slugByDay = {
    1: "sky-hook", 2: "parisian-prep", 3: "academy-of-basketball", 4: "european-team",
    5: "european-circuit", 6: "winter-cup",
    0: ["paris", "montreal", "toronto"][week % 3],
  };
  const p = getProject(slugByDay[day]);
  if (!p) return null;
  const to = p.category === "ligue" ? "/ligue" : `/${p.category}/${p.slug}`;
  const th = p.theme;
  return (
    <section className="py-24 md:py-32 max-w-[1400px] mx-auto px-5 md:px-10" data-testid="project-of-day">
      <Reveal>
        <MonoTag style={{ color: "#F6B02C" }}>
          ✦ {t({ fr: "Le projet du jour", en: "Project of the day", es: "El proyecto del día" })} · {t(DAY_NAMES)[day]} ✦
        </MonoTag>
        <div className="mt-6 relative overflow-hidden rounded-lg border border-white/10 grid grid-cols-1 lg:grid-cols-12"
          style={{ backgroundColor: th.bg }}>
          <div className="relative z-10 lg:col-span-7 p-8 md:p-12 flex flex-col justify-center">
            <div className="flex flex-wrap items-center gap-3">
              <MonoTag className="border border-white/25 rounded-full px-3 py-1.5 text-white/70">{p.code}</MonoTag>
              <MonoTag style={{ color: th.accent }}>{t(p.tag)}</MonoTag>
            </div>
            <h2 className="font-display uppercase leading-[0.85] text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5">{p.name}</h2>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mt-4">{p.city}</p>
            <p className="font-body text-white/80 mt-5 max-w-xl leading-relaxed line-clamp-3">{t(p.desc)}</p>
            <div className="mt-8">
              <Link to={to} data-testid="project-of-day-link"
                className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-heading font-semibold uppercase text-sm tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
                style={{ backgroundColor: th.accent }}>
                {t({ fr: "Découvrir le projet", en: "Discover the project", es: "Descubrir el proyecto" })} <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
          <div className="relative lg:col-span-5 min-h-[260px]">
            <img src={p.img} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${th.bg}, transparent 55%)` }} />
            {p.logo && (
              <img src={p.logo} alt={p.name} className="absolute bottom-5 right-5 h-20 w-20 object-contain rounded-xl bg-white p-1.5 shadow-2xl" />
            )}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default function Home() {
  useLenis();
  const { t } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <MiniSite theme={THEMES.base}>
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen min-h-[680px] overflow-hidden grain">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={IMG.heroDunk} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060B14] via-[#060B14]/45 to-[#060B14]/70" />
        </motion.div>
        <div className="absolute top-[20%] inset-x-0 z-[5] opacity-[0.16] pointer-events-none">
          <Marquee autoFill speed={30} gradient={false}>
            <span className="font-display text-[11vw] uppercase text-stroke-thick mx-8 whitespace-nowrap">AIR SPORTS TRAVEL</span>
            <PlaneIcon size={56} className="mx-8 text-white/70 rotate-90" />
          </Marquee>
        </div>
        <CourtLines className="-left-60 -bottom-60 w-[680px] h-[680px] z-[5]" opacity={0.1} />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-6 font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/70">
            <span style={{ color: "#F6B02C" }}>AST-001</span>
            {ROUTE.map((c, i) => (
              <React.Fragment key={c}>
                {i > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="w-8 border-t border-dashed border-white/40" />
                    <PlaneIcon size={12} className="text-white/60 rotate-90" />
                    <span className="w-8 border-t border-dashed border-white/40" />
                  </span>
                )}
                <span>{c}</span>
              </React.Fragment>
            ))}
          </motion.div>
          <MaskLines lines={t(HOME.heroLines)}
            className="font-display uppercase leading-[0.82] text-white text-6xl sm:text-7xl lg:text-8xl xl:text-9xl tracking-tight" />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }}
            className="font-body text-neutral-300 max-w-xl mt-8 text-base md:text-lg">
            {t(HOME.heroSub)}
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
            className="flex flex-wrap gap-4 mt-8">
            <Link to="/programmes" data-testid="hero-cta-programs"
              className="inline-flex items-center gap-2 bg-flame hover:bg-flame-hover text-white rounded-full px-8 py-4 font-heading font-semibold uppercase text-sm tracking-wide transition-colors duration-300">
              {t(UI.cta.all_programs)} <ArrowUpRight size={18} />
            </Link>
            <a href="#contact" data-testid="hero-cta-contact"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-flame text-white rounded-full px-8 py-4 font-heading font-semibold uppercase text-sm tracking-wide transition-colors duration-300">
              {t(UI.cta.contact)}
            </a>
          </motion.div>
        </div>
      </section>

      {/* FLIGHT INFO BAR */}
      <div className="border-y border-white/10 bg-black/40">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex flex-wrap gap-x-10 gap-y-2 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-white/60">
          <span>{t(TRAVEL.flight)}: AST-001</span>
          <span>{t(TRAVEL.destination)}: {t({ fr: "Le monde entier", en: "The whole world", es: "El mundo entero" })}</span>
          <span>{t(TRAVEL.gate)}: BASKETBALL</span>
          <span className="blink" style={{ color: "#F6B02C" }}>● {t(TRAVEL.boarding)}</span>
        </div>
      </div>

      {/* WINTER CUP — LE GRAND PROJET */}
      <WinterCupFeature />

      {/* MANIFESTO / CHAPTERS */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <CourtLines className="-right-64 top-0 w-[720px] h-[720px]" opacity={0.05} />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <MonoTag style={{ color: "#F6B02C" }}>MANIFESTE / AST</MonoTag>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.9] max-w-3xl mt-4">
              {t(HOME.manifesto)}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            {HOME.chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12} className="border-t border-white/15 pt-6">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-flame text-5xl">{c.n}</span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/30">AST/{c.n}</span>
                </div>
                <h3 className="font-heading text-2xl font-semibold mt-4">{t(c.t)}</h3>
                <p className="font-body text-neutral-400 mt-3">{t(c.d)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 py-6 bg-black/30">
        <Marquee autoFill speed={45} gradient={false}>
          {t(HOME.marquee).map((w, i) => (
            <span key={i} className="flex items-center font-display text-4xl md:text-6xl uppercase mx-8 text-white">
              {w} <PlaneIcon size={30} className="ml-8 rotate-90" style={{ color: "#F6B02C" }} />
            </span>
          ))}
        </Marquee>
      </div>

      {/* PROJET DU JOUR */}
      <ProjectOfDay />

      {/* QUI SOMMES-NOUS */}
      <section className="py-24 md:py-32 max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6 relative">
          <div className="relative overflow-hidden rounded-lg">
            <img src={IMG.coach} alt="Coach & jeunes joueurs" className="w-full h-[500px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#060B14]/60 to-transparent" />
            <div className="absolute bottom-5 left-5 flex items-center gap-3 bg-black/60 backdrop-blur px-4 py-3 rounded-full">
              <ShieldCheck className="text-flame" size={20} />
              <span className="font-heading text-sm">100% encadré & sécurisé</span>
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-6">
          <Reveal>
            <MonoTag className="text-flame">{t(HOME.aboutTitle)}</MonoTag>
            <p className="font-body text-neutral-300 mt-6 text-lg leading-relaxed">{t(HOME.aboutText)}</p>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-10">
            {HOME.stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="font-display text-4xl" style={{ color: "#F6B02C" }}>{s.v}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500 mt-2">{t(s.l)}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COUNTDOWN WINTER CUP */}
      <Countdown />

      {/* ALUMNI EN VEDETTE */}
      <AlumniSpotlight />

      {/* TÉMOIGNAGES PARENTS */}
      <section className="py-24 md:py-32 max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <MonoTag style={{ color: "#F6B02C" }}>{t(TESTIMONIALS.overline)}</MonoTag>
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none mt-4">{t(TESTIMONIALS.title)}</h2>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                <span className="font-display text-4xl" style={{ color: "#F6B02C" }}>{TESTIMONIALS.rating}</span>
                <div className="flex">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} size={16} fill="#F6B02C" stroke="none" />
                  ))}
                </div>
              </div>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50 mt-1">{t(TESTIMONIALS.ratingLabel)}</p>
            </div>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {TESTIMONIALS.list.map((x, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div data-testid={`testimonial-${i}`} className="h-full rounded-lg border border-white/10 p-8"
                style={{ backgroundColor: "#101A30" }}>
                <div className="flex">
                  {[...Array(x.stars)].map((_, s) => (
                    <Star key={s} size={15} fill="#F6B02C" stroke="none" />
                  ))}
                </div>
                <p className="font-body text-white/85 italic mt-5 leading-relaxed">« {t(x.quote)} »</p>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <p className="font-heading font-semibold">{x.name}</p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50 mt-1">{t(x.role)}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* DEPARTURES BOARD */}
      <section className="pb-24 md:pb-32 max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <MonoTag style={{ color: "#F6B02C" }}>✦ {t(TRAVEL.departures)} — DEPARTURES ✦</MonoTag>
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none mt-4 mb-10">{t(HOME.overviewTitle)}</h2>
        </Reveal>
        <div className="border border-white/10 rounded-md overflow-hidden bg-black/30">
          <div className="hidden md:grid grid-cols-12 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 px-6 py-3.5 border-b border-white/10 bg-white/[0.03]">
            <span className="col-span-2">{t(TRAVEL.flight)}</span>
            <span className="col-span-4">{t(TRAVEL.program)}</span>
            <span className="col-span-3">{t(TRAVEL.destination)}</span>
            <span className="col-span-2">{t(TRAVEL.status)}</span>
            <span className="col-span-1" />
          </div>
          {DEPARTURES.map((d, i) => (
            <Reveal key={d.code} delay={i * 0.04} y={16}>
              <Link to={d.to} data-testid={`departure-${d.code}`}
                className="group grid grid-cols-2 md:grid-cols-12 items-center gap-y-1 px-6 py-4 border-b border-white/5 hover:bg-white/[0.05] transition-colors duration-200">
                <span className="font-mono text-xs text-white/50 md:col-span-2">{d.code}</span>
                <span className="font-heading font-semibold uppercase tracking-wide col-span-2 md:col-span-4 order-first md:order-none">{d.name}</span>
                <span className="font-mono text-[11px] text-white/60 md:col-span-3">{d.dest}</span>
                <span className={`font-mono text-[10px] uppercase tracking-[0.2em] text-right md:text-left md:col-span-2 ${d.status === "boarding" ? "blink" : ""}`}
                  style={{ color: d.color }}>
                  {t(TRAVEL[d.status])}
                </span>
                <ArrowUpRight size={16} className="hidden md:block md:col-span-1 justify-self-end opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <ContactSection programs={PROJECTS.map((p) => p.name)} />
    </MiniSite>
  );
}
